import { createFileRoute } from "@tanstack/react-router"

import { CodeBlock } from "@/components/dev/CodeBlock"
import { PropsTable } from "@/components/dev/PropsTable"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"

export const Route = createFileRoute("/qr/$qrLookupID/dev/atoms/card")({
  staticData: {
    pageName: "Card Component",
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <>
      <style>{`
        .dev-container > *:not(:first-child) {
          margin-top: var(--space-5);
        }
      `}</style>
      <div className="dev-container">
        <Card>
          <Heading level={1} as={2}>
            Card Component
          </Heading>
          <Text>
            The Card component is a versatile container that provides automatic
            padding, borders, shadows, and theme-aware styling. It's the primary
            building block for grouping related content and creating visual
            hierarchy.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Basic Card (Default)
          </Heading>
          <Text>
            The default card with neutral styling - perfect for most content
            containers
          </Text>
          <Card>
            <Heading level={3} as={4}>
              Card Title
            </Heading>
            <Text>
              This is a basic card with neutral styling. It includes automatic
              padding, a subtle border, and shadow for depth.
            </Text>
          </Card>
          <CodeBlock>{`<Card>
  <Heading level={3} as={4}>Card Title</Heading>
  <Text>Card content goes here...</Text>
</Card>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Solid Variant with Hues
          </Heading>
          <Text>
            Use the solid variant with different hues for semantic meaning and
            visual emphasis
          </Text>
          <div>
            <Text level={2} className="semi-bold">
              Primary Solid
            </Text>
            <Card hue="primary" variant="solid">
              <Heading level={3} as={4}>
                Primary Hue
              </Heading>
              <Text>Used for primary actions or highlighted content</Text>
            </Card>
            <CodeBlock>{`<Card hue="primary" variant="solid">
  <Heading level={3} as={4}>Primary Hue</Heading>
  <Text>Used for primary actions or highlighted content</Text>
</Card>`}</CodeBlock>
          </div>

          <div>
            <Text level={2} className="semi-bold">
              Secondary Solid
            </Text>
            <Card hue="secondary" variant="solid">
              <Heading level={3} as={4}>
                Secondary Hue
              </Heading>
              <Text>Used for secondary or supporting content</Text>
            </Card>
            <CodeBlock>{`<Card hue="secondary" variant="solid">
  <Heading level={3} as={4}>Secondary Hue</Heading>
  <Text>Used for secondary or supporting content</Text>
</Card>`}</CodeBlock>
          </div>

          <div>
            <Text level={2} className="semi-bold">
              Success Solid
            </Text>
            <Card hue="success" variant="solid">
              <Heading level={3} as={4}>
                Success Hue
              </Heading>
              <Text>Used for successful operations or positive feedback</Text>
            </Card>
            <CodeBlock>{`<Card hue="success" variant="solid">
  <Heading level={3} as={4}>Success Hue</Heading>
  <Text>Used for successful operations or positive feedback</Text>
</Card>`}</CodeBlock>
          </div>

          <div>
            <Text level={2} className="semi-bold">
              Caution Solid
            </Text>
            <Card hue="caution" variant="solid">
              <Heading level={3} as={4}>
                Caution Hue
              </Heading>
              <Text>Used for warnings or important notices</Text>
            </Card>
            <CodeBlock>{`<Card hue="caution" variant="solid">
  <Heading level={3} as={4}>Caution Hue</Heading>
  <Text>Used for warnings or important notices</Text>
</Card>`}</CodeBlock>
          </div>

          <div>
            <Text level={2} className="semi-bold">
              Danger Solid
            </Text>
            <Card hue="danger" variant="solid">
              <Heading level={3} as={4}>
                Danger Hue
              </Heading>
              <Text>Used for errors or destructive actions</Text>
            </Card>
            <CodeBlock>{`<Card hue="danger" variant="solid">
  <Heading level={3} as={4}>Danger Hue</Heading>
  <Text>Used for errors or destructive actions</Text>
</Card>`}</CodeBlock>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            All Solid Variants Together
          </Heading>
          <Text>Visual comparison of all available solid hues</Text>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "var(--space-3)",
            }}
          >
            <Card hue="primary" variant="solid">
              <Text className="semi-bold">Primary</Text>
              <Text level={3}>Main actions</Text>
            </Card>
            <Card hue="secondary" variant="solid">
              <Text className="semi-bold">Secondary</Text>
              <Text level={3}>Supporting content</Text>
            </Card>
            <Card hue="success" variant="solid">
              <Text className="semi-bold">Success</Text>
              <Text level={3}>Positive feedback</Text>
            </Card>
            <Card hue="caution" variant="solid">
              <Text className="semi-bold">Caution</Text>
              <Text level={3}>Warnings</Text>
            </Card>
            <Card hue="danger" variant="solid">
              <Text className="semi-bold">Danger</Text>
              <Text level={3}>Errors</Text>
            </Card>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Nested Cards
          </Heading>
          <Text>Cards can be nested for complex layouts</Text>
          <Card hue="primary" variant="solid">
            <Heading level={3} as={4}>
              Parent Card
            </Heading>
            <Text>This card contains another card inside it</Text>
            <Card>
              <Text className="semi-bold">Nested Card</Text>
              <Text level={3}>
                Nested cards are useful for grouping related sub-content
              </Text>
            </Card>
          </Card>
          <CodeBlock>{`<Card hue="primary" variant="solid">
  <Heading level={3} as={4}>Parent Card</Heading>
  <Text>This card contains another card inside it</Text>
  <Card>
    <Text className="semi-bold">Nested Card</Text>
    <Text level={3}>Nested cards work well together</Text>
  </Card>
</Card>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "hue",
                type: '"primary" | "secondary" | "neutral" | "success" | "caution" | "danger"',
                default: '"neutral"',
                description:
                  "Semantic color theme (only visible with variant='solid')",
              },
              {
                name: "variant",
                type: '"default" | "solid"',
                default: '"default"',
                description: "Visual style - use 'solid' to apply hue coloring",
              },
              {
                name: "className",
                type: "string",
                default: "undefined",
                description: "Additional CSS classes to apply",
              },
              {
                name: "children",
                type: "ReactNode",
                default: "undefined",
                description: "Card content",
              },
            ]}
          />
          <Text>
            Note: Card extends standard HTML div attributes, so it supports all
            native props like <code>onClick</code>, <code>style</code>,{" "}
            <code>id</code>, etc.
          </Text>
        </Card>
      </div>
    </>
  )
}
